import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../systems/auth-context';
import { downloadReviewChangelog, fetchReviews, resolveReview } from '../../systems/review-api';
import { fetchScenarioCatalog, loadScenario } from '../scenario-editor/scenario-api';
import { PreviewModal } from '../scenario-editor/PreviewModal';
import { Review, ReviewStatus } from '../../types/review';
import { Scenario } from '../../types';
import { useWorkspace } from '../../systems/workspace-context';
import styles from './reviews-dashboard.module.css';

interface ReviewsDashboardProps {
  onBack: () => void;
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleString('fr-FR');
  } catch {
    return value;
  }
}

function truncate(text: string, max = 90): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}…`;
}

function formatContextLine(review: Review): string | null {
  const parts = [review.mission_label, review.chapter_label].filter(Boolean);
  if (parts.length === 0) return null;
  return parts.join(' / ');
}

function toInputDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function defaultChangelogFromDate(): string {
  const date = new Date();
  return toInputDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

interface CatalogEntry {
  id: string;
  title: string;
  version?: string;
  releaseToProd?: boolean;
}

function formatScenarioLabel(item: { title: string; version?: string }): string {
  return item.version ? `${item.title} (v${item.version})` : item.title;
}

function isPlayerVisibleScenario(item: CatalogEntry): boolean {
  return item.releaseToProd !== false;
}

export const ReviewsDashboard: React.FC<ReviewsDashboardProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { workspaceId } = useWorkspace();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [catalog, setCatalog] = useState<CatalogEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | ReviewStatus>('open');
  const [scenarioFilter, setScenarioFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewScenario, setPreviewScenario] = useState<Scenario | null>(null);
  const [previewScenarioId, setPreviewScenarioId] = useState<string | null>(null);
  const [previewSceneId, setPreviewSceneId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [downloadingChangelog, setDownloadingChangelog] = useState(false);
  const [changelogFrom, setChangelogFrom] = useState(defaultChangelogFromDate);
  const [changelogTo, setChangelogTo] = useState(() => toInputDate(new Date()));

  const visibleCatalog = useMemo(
    () => catalog.filter(isPlayerVisibleScenario),
    [catalog]
  );

  const scenarioTitles = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of catalog) {
      map.set(item.id, formatScenarioLabel(item));
    }
    return map;
  }, [catalog]);

  const loadReviews = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchReviews({
        scenario_id: scenarioFilter || undefined,
        status: statusFilter === 'all' ? undefined : statusFilter,
      });
      setReviews(data);
      setSelectedId((prev) => {
        if (prev && data.some((r) => r.id === prev)) return prev;
        return data[0]?.id ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du chargement');
    } finally {
      setLoading(false);
    }
  }, [user, scenarioFilter, statusFilter, workspaceId]);

  useEffect(() => {
    setScenarioFilter('');
  }, [workspaceId]);

  useEffect(() => {
    if (!user) return;
    fetchScenarioCatalog()
      .then((items) => {
        setCatalog(
          items.map((item) => ({
            id: item.scenarioId,
            title: item.title,
            version: item.version,
            releaseToProd: item.releaseToProd,
          }))
        );
      })
      .catch((err) => console.error(err));
  }, [user, workspaceId]);

  useEffect(() => {
    if (visibleCatalog.length === 0) return;
    setScenarioFilter((prev) => {
      if (prev && visibleCatalog.some((item) => item.id === prev)) return prev;
      return visibleCatalog[0].id;
    });
  }, [visibleCatalog]);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const selectedReview = reviews.find((r) => r.id === selectedId) ?? null;
  const changelogScenarioId = scenarioFilter || selectedReview?.scenario_id || '';

  const handleDownloadChangelog = async () => {
    if (!user || !changelogScenarioId || !changelogFrom || !changelogTo) return;
    if (changelogFrom > changelogTo) {
      setError('La date de début doit être antérieure ou égale à la date de fin.');
      return;
    }
    setDownloadingChangelog(true);
    setError(null);
    try {
      await downloadReviewChangelog(changelogScenarioId, changelogFrom, changelogTo);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec du téléchargement du changelog');
    } finally {
      setDownloadingChangelog(false);
    }
  };

  const handlePreview = async () => {
    if (!user || !selectedReview) return;
    setPreviewLoading(true);
    setError(null);
    try {
      const scenario = await loadScenario(selectedReview.scenario_id);
      setPreviewScenario(scenario);
      setPreviewScenarioId(selectedReview.scenario_id);
      setPreviewSceneId(selectedReview.scene_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger le scénario');
    } finally {
      setPreviewLoading(false);
    }
  };

  const openCloseModal = () => {
    setResolutionNote('');
    setCloseModalOpen(true);
  };

  const handleResolve = async () => {
    if (!user || !selectedReview || selectedReview.status === 'resolved') return;
    setResolving(true);
    setError(null);
    try {
      const updated = await resolveReview(selectedReview.id, {
        status: 'resolved',
        resolution_note: resolutionNote.trim() || null,
      });
      setReviews((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
      setCloseModalOpen(false);
      setResolutionNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Échec de la mise à jour');
    } finally {
      setResolving(false);
    }
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1>Reviews — retours test interne</h1>
        <button type="button" className={styles.backBtn} onClick={onBack}>
          Retour
        </button>
      </header>

      {error && <p className={styles.error}>{error}</p>}

      <section className={styles.changelogPanel}>
        <h2>Exporter le changelog</h2>
        <p className={styles.changelogHint}>
          Génère un fichier .md avec les tickets <strong>résolus</strong> créés sur la
          période choisie.
        </p>
        <div className={styles.changelogControls}>
          <select
            value={scenarioFilter}
            onChange={(e) => setScenarioFilter(e.target.value)}
            aria-label="Scénario pour le changelog"
            disabled={visibleCatalog.length === 0}
          >
            {visibleCatalog.map((item) => (
              <option key={item.id} value={item.id}>
                {formatScenarioLabel(item)}
              </option>
            ))}
          </select>

          <label className={styles.dateField}>
            <span>Du</span>
            <input
              type="date"
              value={changelogFrom}
              onChange={(e) => setChangelogFrom(e.target.value)}
            />
          </label>

          <label className={styles.dateField}>
            <span>Au</span>
            <input
              type="date"
              value={changelogTo}
              onChange={(e) => setChangelogTo(e.target.value)}
            />
          </label>

          <button
            type="button"
            className={styles.btnPrimary}
            onClick={handleDownloadChangelog}
            disabled={!changelogScenarioId || !changelogFrom || !changelogTo || downloadingChangelog}
          >
            {downloadingChangelog ? 'Génération…' : 'Télécharger le .md'}
          </button>
        </div>
      </section>

      <div className={styles.filters}>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | ReviewStatus)}
        >
          <option value="all">Tous les statuts</option>
          <option value="open">Ouverts</option>
          <option value="resolved">Résolus</option>
        </select>

        <select
          value={scenarioFilter}
          onChange={(e) => setScenarioFilter(e.target.value)}
          disabled={visibleCatalog.length === 0}
        >
          {visibleCatalog.map((item) => (
            <option key={item.id} value={item.id}>
              {formatScenarioLabel(item)}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <p className={styles.loading}>Chargement…</p>
      ) : (
        <div className={styles.layout}>
          <section className={styles.panel}>
            <h2>Commentaires ({reviews.length})</h2>
            <div className={styles.reviewList}>
              {reviews.length === 0 ? (
                <p className={styles.detailEmpty}>Aucun commentaire pour ces filtres.</p>
              ) : (
                reviews.map((review) => (
                  <button
                    key={review.id}
                    type="button"
                    className={`${styles.reviewItem} ${
                      review.id === selectedId ? styles.reviewItemSelected : ''
                    }`}
                    onClick={() => setSelectedId(review.id)}
                  >
                    <div className={styles.reviewMeta}>
                      <span>
                        {review.ticket_number != null ? `ticket ${review.ticket_number} · ` : ''}
                        {review.user_email}
                      </span>
                      <span>
                        {review.status === 'open' ? (
                          <span className={styles.badgeOpen}>Ouvert</span>
                        ) : (
                          <span className={styles.badgeResolved}>Résolu</span>
                        )}
                      </span>
                    </div>
                    <div className={styles.reviewExcerpt}>{truncate(review.body)}</div>
                    <div className={styles.reviewMeta}>
                      <span>{scenarioTitles.get(review.scenario_id) ?? review.scenario_id}</span>
                      <span>{formatDate(review.created_at)}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </section>

          <section className={styles.panel}>
            <h2>Détail</h2>
            {!selectedReview ? (
              <p className={styles.detailEmpty}>Sélectionnez un commentaire.</p>
            ) : (
              <div className={styles.detailBody}>
                {selectedReview.ticket_number != null && (
                  <div className={styles.detailRow}>
                    <strong>Ticket :</strong> {selectedReview.ticket_number}
                  </div>
                )}
                <div className={styles.detailRow}>
                  <strong>Auteur :</strong> {selectedReview.user_email}
                </div>
                <div className={styles.detailRow}>
                  <strong>Date :</strong> {formatDate(selectedReview.created_at)}
                </div>
                <div className={styles.detailRow}>
                  <strong>Scénario :</strong>{' '}
                  {scenarioTitles.get(selectedReview.scenario_id) ?? selectedReview.scenario_id}
                </div>
                {formatContextLine(selectedReview) && (
                  <div className={styles.detailRow}>
                    <strong>Contexte :</strong> {formatContextLine(selectedReview)}
                  </div>
                )}
                <div className={styles.detailRow}>
                  <strong>Scène :</strong> <code>{selectedReview.scene_id}</code>
                </div>
                {selectedReview.mission_id && (
                  <div className={styles.detailRow}>
                    <strong>Mission (id) :</strong> {selectedReview.mission_id}
                  </div>
                )}
                {selectedReview.scene_excerpt && (
                  <div className={styles.sceneExcerpt}>({selectedReview.scene_excerpt})</div>
                )}
                <div className={styles.commentText}>{selectedReview.body}</div>
                {selectedReview.resolution_note && (
                  <div className={styles.detailRow}>
                    <strong>Réponse :</strong> {selectedReview.resolution_note}
                  </div>
                )}

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={handlePreview}
                    disabled={previewLoading}
                  >
                    {previewLoading ? 'Chargement…' : 'Aperçu à cette scène'}
                  </button>
                  {selectedReview.status === 'open' && (
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={openCloseModal}
                      disabled={resolving}
                    >
                      Fermer le ticket
                    </button>
                  )}
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {closeModalOpen && selectedReview && (
        <div
          className={styles.modalOverlay}
          onClick={resolving ? undefined : () => setCloseModalOpen(false)}
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <header className={styles.modalHeader}>
              <h3>
                Fermer le ticket
                {selectedReview.ticket_number != null ? ` ${selectedReview.ticket_number}` : ''}
              </h3>
              <button
                type="button"
                className={styles.modalCloseBtn}
                onClick={() => setCloseModalOpen(false)}
                disabled={resolving}
                aria-label="Fermer"
              >
                ×
              </button>
            </header>
            <div className={styles.modalBody}>
              <label className={styles.modalLabel} htmlFor="resolution-note">
                Réponse / note de clôture (optionnel)
              </label>
              <textarea
                id="resolution-note"
                className={styles.modalTextarea}
                value={resolutionNote}
                onChange={(e) => setResolutionNote(e.target.value)}
                placeholder='Ex. changé comme suggéré'
                maxLength={1000}
                disabled={resolving}
                autoFocus
              />
            </div>
            <footer className={styles.modalFooter}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setCloseModalOpen(false)}
                disabled={resolving}
              >
                Annuler
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleResolve}
                disabled={resolving}
              >
                {resolving ? 'Fermeture…' : 'Fermer le ticket'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {previewScenario && previewScenarioId && previewSceneId && (
        <PreviewModal
          scenario={previewScenario}
          scenarioId={previewScenarioId}
          startSceneId={previewSceneId}
          onClose={() => {
            setPreviewScenario(null);
            setPreviewScenarioId(null);
            setPreviewSceneId(null);
          }}
        />
      )}
    </div>
  );
};

export default ReviewsDashboard;
